import React, { useState } from 'react';
import './CrochetOrderForm.css';

// Import Navbar and Footer from the correct paths
import Navbar from '../Website/Navbar';
import Footer from '../Website/Footer';
// Import Logo from assets/images
import JackLogo from '../../assets/images/jackLogo.png';

// Base URL for API - Change this to your production URL when deploying
const BASE_URL = 'https://zitechsolutions.co.tz';
// For production, change to: const BASE_URL = 'https://zitechsolutions.co.tz';

const CrochetOrderForm = () => {
  // State for customer information
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    region: '',
    deliveryAddress: ''
  });

  // State for order details
  const [orderDetails, setOrderDetails] = useState({
    productType: '',
    preferredColor: '',
    size: '',
    quantity: 1,
    deliveryDate: '',
    additionalNotes: '',
    customProductType: '' // New field for custom product type
  });

  // State for multiple image uploads
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const MAX_IMAGES = 3;

  // State for form submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // State for showing custom product input
  const [showCustomProduct, setShowCustomProduct] = useState(false);

  // Handle customer info changes
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle order details changes
  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    setOrderDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle product type change
  const handleProductTypeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomProduct(true);
      setOrderDetails(prev => ({
        ...prev,
        productType: ''
      }));
    } else {
      setShowCustomProduct(false);
      setOrderDetails(prev => ({
        ...prev,
        productType: value
      }));
    }
  };

  // Handle multiple image uploads
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - uploadedImages.length;
    const filesToAdd = files.slice(0, remainingSlots);

    if (filesToAdd.length === 0) {
      alert(`Unaweza kupakia picha ${MAX_IMAGES} pekee.`);
      return;
    }

    const newImages = [...uploadedImages];
    const newPreviews = [...imagePreviews];

    filesToAdd.forEach(file => {
      newImages.push(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        setImagePreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });

    setUploadedImages(newImages);
    // Reset the input
    e.target.value = '';
  };

  // Remove a specific image
  const removeImage = (index) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setUploadedImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Handle form submission - Send to Django Backend with images as base64
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSubmissionStatus(null);

    // Determine the final product type
    let finalProductType = orderDetails.productType;
    let customProductType = '';
    if (showCustomProduct && orderDetails.customProductType) {
      finalProductType = 'Custom Order'; // Send "Custom Order" to backend
      customProductType = orderDetails.customProductType; // Send the custom name
    }

    // Format the date for delivery
    const formattedDate = orderDetails.deliveryDate 
      ? new Date(orderDetails.deliveryDate).toLocaleDateString('sw-TZ', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : 'Not specified';

    // Convert images to base64
    let imageBase64 = [];
    if (uploadedImages.length > 0) {
      try {
        const imagePromises = uploadedImages.map((file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.readAsDataURL(file);
          });
        });
        imageBase64 = await Promise.all(imagePromises);
      } catch (error) {
        console.error('Error converting images to base64:', error);
      }
    }

    // Prepare form data for backend
    const formData = {
      // Customer Information
      fullName: customerInfo.fullName,
      phoneNumber: customerInfo.phoneNumber,
      email: customerInfo.email || '',
      region: customerInfo.region,
      deliveryAddress: customerInfo.deliveryAddress,
      
      // Order Details
      productType: finalProductType, // Send "Custom Order" if custom
      customProductType: customProductType, // Send the custom product name
      preferredColor: orderDetails.preferredColor,
      size: orderDetails.size || 'Not specified',
      quantity: orderDetails.quantity,
      deliveryDate: formattedDate,
      additionalNotes: orderDetails.additionalNotes || 'None',
      
      // Images - send base64 data
      imageCount: uploadedImages.length,
      imageNames: uploadedImages.map(img => img.name).join(', ') || 'No images uploaded',
      images: imageBase64, // Array of base64 encoded images
      
      // Timestamp
      submittedAt: new Date().toISOString()
    };

    try {
      // Send to Django backend using BASE_URL
      const response = await fetch(`${BASE_URL}/api/send-crochet-order/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit order');
      }
      
      // Success - Reset form
      setIsSubmitting(false);
      setSubmissionStatus('success');
      
      // Reset form fields
      setCustomerInfo({
        fullName: '',
        phoneNumber: '',
        email: '',
        region: '',
        deliveryAddress: ''
      });
      setOrderDetails({
        productType: '',
        preferredColor: '',
        size: '',
        quantity: 1,
        deliveryDate: '',
        additionalNotes: '',
        customProductType: ''
      });
      setUploadedImages([]);
      setImagePreviews([]);
      setShowCustomProduct(false);
      
      // Show success message
      alert('Asante kwa kuagiza. Tutawasiliana nawe ndani ya masaa 24 kuthibitisha oda.');
      
    } catch (error) {
      console.error('Error submitting order:', error);
      setIsSubmitting(false);
      setSubmissionStatus('error');
      setErrorMessage(error.message || 'Kuna tatizo katika kutuma oda. Tafadhali jaribu tena au wasiliana nasi moja kwa moja.');
    }
  };

  const productTypes = [
    'Bucket Hat',
    'Mesh Hat',
    'Handbag',
    'Wallet/Pouch',
    'Sweater',
    'Top',
    'Custom Order'
  ];

  // SVG Icons
  const UserIcon = () => (
    <svg className="section-icon" viewBox="0 0 24 24" width="24" height="24">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    </svg>
  );

  const YarnIcon = () => (
    <svg className="section-icon" viewBox="0 0 24 24" width="24" height="24">
      <path d="M12 2C8 2 4 4 4 8c0 3 2 5 4 7s4 4 4 7c0-3 2-5 4-7s4-4 4-7c0-4-4-6-8-6z" stroke="currentColor" strokeWidth="2" fill="none"/>
      <path d="M8 8l8 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M8 12l6 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const ImageIcon = () => (
    <svg className="section-icon" viewBox="0 0 24 24" width="24" height="24">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
      <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2" fill="none"/>
      <path d="M21 15l-5-5-5 5-4-4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div className="crochet-order-page">
      {/* Navbar */}
      <Navbar />

      {/* Main Content */}
      <main className="crochet-form-main">
        <div className="crochet-form-wrapper">
          {/* Logo Section */}
          <div className="form-logo-container">
            <img 
              src={JackLogo} 
              alt="Crochet by Jacqueline" 
              className="form-logo"
            />
          </div>

          <div className="form-header">
            <h1 className="form-title">Fomu ya Kuagiza Crochet</h1>
            <p className="form-subtitle">Jaza taarifa zote ili kutuma oda yako</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Customer Information Section */}
            <section className="form-section">
              <h2 className="section-title">
                <UserIcon />
                Taarifa za Mteja
              </h2>

              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="fullName">
                    Jina Kamili <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={customerInfo.fullName}
                    onChange={handleCustomerChange}
                    required
                    className="form-control"
                    placeholder="Ingiza jina lako kamili"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    Namba ya Simu <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={customerInfo.phoneNumber}
                    onChange={handleCustomerChange}
                    required
                    className="form-control"
                    placeholder="Ingiza namba ya simu"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Barua Pepe <span className="optional">(Optional)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleCustomerChange}
                    className="form-control"
                    placeholder="Ingiza barua pepe"
                  />
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="region">
                    Mkoa/Mji <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="region"
                    name="region"
                    value={customerInfo.region}
                    onChange={handleCustomerChange}
                    required
                    className="form-control"
                    placeholder="Ingiza mkoa au mji wako"
                  />
                </div>

                <div className="form-group-full">
                  <label htmlFor="deliveryAddress">
                    Anuani ya Kufikishia Bidhaa <span className="required">*</span>
                  </label>
                  <textarea
                    id="deliveryAddress"
                    name="deliveryAddress"
                    value={customerInfo.deliveryAddress}
                    onChange={handleCustomerChange}
                    required
                    className="form-control"
                    rows="2"
                    placeholder="Ingiza anuani kamili ya kufikishia bidhaa"
                  />
                </div>
              </div>
            </section>

            {/* Order Details Section */}
            <section className="form-section">
              <h2 className="section-title">
                <YarnIcon />
                Maelezo ya Oda
              </h2>

              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="productType">
                    Aina ya Bidhaa <span className="required">*</span>
                  </label>
                  <select
                    id="productType"
                    name="productType"
                    value={showCustomProduct ? 'custom' : orderDetails.productType}
                    onChange={handleProductTypeChange}
                    required
                    className="form-control"
                  >
                    <option value="">-- Chagua --</option>
                    {productTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                    <option value="custom">➕ Custom / Nyingine</option>
                  </select>
                </div>

                {/* Custom Product Type Input - Show when "Custom" is selected */}
                {showCustomProduct && (
                  <div className="form-group">
                    <label htmlFor="customProductType">
                      Taja Aina ya Bidhaa Yako <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="customProductType"
                      name="customProductType"
                      value={orderDetails.customProductType}
                      onChange={handleOrderChange}
                      required
                      className="form-control"
                      placeholder="Ingiza aina ya bidhaa unayotaka"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="preferredColor">
                    Rangi <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="preferredColor"
                    name="preferredColor"
                    value={orderDetails.preferredColor}
                    onChange={handleOrderChange}
                    required
                    className="form-control"
                    placeholder="Rangi unayopendelea"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="size">
                    Size <span className="optional">(kama inahitajika)</span>
                  </label>
                  <input
                    type="text"
                    id="size"
                    name="size"
                    value={orderDetails.size}
                    onChange={handleOrderChange}
                    className="form-control"
                    placeholder="Ingiza size"
                  />
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label htmlFor="quantity">
                    Kiasi (Quantity) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={orderDetails.quantity}
                    onChange={handleOrderChange}
                    min="1"
                    required
                    className="form-control"
                    placeholder="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="deliveryDate">
                    Tarehe ya Kupokea <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="deliveryDate"
                    name="deliveryDate"
                    value={orderDetails.deliveryDate}
                    onChange={handleOrderChange}
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="additionalNotes">
                    Maelezo ya Ziada
                  </label>
                  <input
                    type="text"
                    id="additionalNotes"
                    name="additionalNotes"
                    value={orderDetails.additionalNotes}
                    onChange={handleOrderChange}
                    className="form-control"
                    placeholder="Maelezo yoyote ya ziada"
                  />
                </div>
              </div>
            </section>

            {/* Image Upload Section - Multiple Images */}
            <section className="form-section">
              <h2 className="section-title">
                <ImageIcon />
                Picha za Mfano
              </h2>

              <div className="form-grid-3">
                <div className="form-group-full">
                  <label>
                    Pakia picha za design unayotaka <span className="optional">(Upeo wa picha {MAX_IMAGES})</span>
                  </label>
                  
                  {uploadedImages.length < MAX_IMAGES && (
                    <div className="file-upload-wrapper">
                      <label className="file-upload-label">
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path d="M12 4v16M4 12h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Chagua Picha ({uploadedImages.length}/{MAX_IMAGES})
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="file-upload-input"
                          multiple
                        />
                      </label>
                      <span className="upload-info">
                        {uploadedImages.length === 0 ? 'Bado hakuna picha' : `${uploadedImages.length} ya ${MAX_IMAGES} picha zimepakiwa`}
                      </span>
                    </div>
                  )}

                  {uploadedImages.length >= MAX_IMAGES && (
                    <div className="upload-complete-message">
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <circle cx="12" cy="12" r="10" fill="none" stroke="#2d6a2d" strokeWidth="2" />
                        <path d="M7 12l3 3 7-7" stroke="#2d6a2d" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <span>Umepakia picha zote {MAX_IMAGES}</span>
                    </div>
                  )}

                  {/* Image Previews Grid */}
                  {imagePreviews.length > 0 && (
                    <div className="image-preview-grid">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="image-preview-item">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="image-preview"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="image-remove-btn"
                            title="Ondoa picha"
                          >
                            ✕
                          </button>
                          <span className="image-counter">Picha {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Inatuma...
                  </>
                ) : (
                  'Submit Order'
                )}
              </button>
              <p className="terms-text">
                Kwa kuwasilisha, unakubali masharti yetu ya huduma
              </p>
            </div>
          </form>

          {/* Success Message */}
          {submissionStatus === 'success' && (
            <div className="success-message">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <circle cx="12" cy="12" r="10" fill="none" stroke="#2d6a2d" strokeWidth="2" />
                <path d="M7 12l3 3 7-7" stroke="#2d6a2d" strokeWidth="2" strokeLinecap="round" />
              </svg>
              ✓ Oda imetumwa! Tutawasiliana nawe hivi karibuni.
            </div>
          )}

          {/* Error Message */}
          {submissionStatus === 'error' && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <circle cx="12" cy="12" r="10" fill="none" stroke="#c0392b" strokeWidth="2" />
                <path d="M12 8v4M12 16h.01" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {errorMessage || 'Kuna tatizo katika kutuma oda. Tafadhali jaribu tena.'}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CrochetOrderForm;